/**
 * @module calculate-units
 * @description Pure utility functions for inventory unit calculations
 *
 * ## Purpose
 *
 * These utilities handle the conversion between string representations
 * of inventory units (as stored in the database) and numeric values
 * needed for calculations.
 *
 * ## Inventory Unit Format
 *
 * Inventory units are stored as strings in format: "{quantity} {unit}"
 * Examples:
 * - "200 Each" - 200 individual items
 * - "500 kg" - 500 kilograms
 * - "150 Litre" - 150 liters
 * - "75 Pair" - 75 pairs
 */

import type { ConsignmentLineItem } from '@/entities/consignments';

/**
 * Parse inventory units from string format to structured data
 *
 * @param inventoryUnits - String in format "200 Each" or similar
 * @returns Parsed quantity and unit type
 *
 * @example
 * ```typescript
 * parseInventoryUnits("200 Each")
 * // Returns: { quantity: 200, unit: "Each" }
 *
 * parseInventoryUnits("500 kg")
 * // Returns: { quantity: 500, unit: "kg" }
 *
 * parseInventoryUnits(undefined)
 * // Returns: { quantity: 0, unit: "" }
 *
 * parseInventoryUnits(null)
 * // Returns: { quantity: 0, unit: "" }
 * ```
 */
export function parseInventoryUnits(inventoryUnits: string | null | undefined): {
  quantity: number;
  unit: string;
} {
  if (!inventoryUnits) return { quantity: 0, unit: '' };

  const match = inventoryUnits.match(/^([\d.]+)\s+(.+)$/);
  if (!match) return { quantity: 0, unit: '' };

  return {
    quantity: parseFloat(match[1]),
    unit: match[2]
  };
}

/**
 * Calculate total units and common unit type for multiple selected lines
 *
 * This function is used to:
 * 1. Sum up total inventory units across selected lines
 * 2. Detect if all lines share the same unit type
 * 3. Return null for unit if lines have mixed unit types
 *
 * @param selectedLineIds - IDs of selected consignment lines
 * @param lines - All available consignment lines
 * @returns Common unit type (or null if mixed) and total units
 *
 * @example
 * ```typescript
 * const lines = [
 *   { id: '1', inventoryUnits: '100 Each', ... },
 *   { id: '2', inventoryUnits: '50 Each', ... },
 *   { id: '3', inventoryUnits: '200 kg', ... }
 * ];
 *
 * // Same unit type
 * calculateSelectedLinesInfo(['1', '2'], lines)
 * // Returns: { unit: 'Each', totalUnits: 150 }
 *
 * // Mixed unit types
 * calculateSelectedLinesInfo(['1', '3'], lines)
 * // Returns: { unit: null, totalUnits: 300 }
 * ```
 */
export function calculateSelectedLinesInfo(
  selectedLineIds: string[],
  lines: ConsignmentLineItem[]
): {
  unit: string | null;
  totalUnits: number;
} {
  if (selectedLineIds.length === 0) {
    return { unit: null, totalUnits: 0 };
  }

  const units = new Set<string>();
  let totalUnits = 0;

  for (const lineId of selectedLineIds) {
    const line = lines.find((l) => l.id === lineId);
    if (!line) continue;

    const { quantity, unit } = parseInventoryUnits(line.inventoryUnits);
    if (quantity > 0 && unit) {
      totalUnits += quantity;
      units.add(unit);
    }
  }

  // Return null if mixed units (more than one type)
  return {
    unit: units.size === 1 ? [...units][0] : null,
    totalUnits
  };
}

/**
 * Calculate total weight and volume for a set of consignment lines
 *
 * Used for pallet summary displays to show aggregate metrics
 *
 * @param lines - Consignment lines to sum
 * @returns Total weight in kg and volume in m³
 *
 * @example
 * ```typescript
 * const lines = [
 *   { weight: "50.5", volume: "0.125", ... },
 *   { weight: 25, volume: 0.075, ... }
 * ];
 *
 * calculateTotals(lines)
 * // Returns: { totalWeight: 75.5, totalVolume: 0.2 }
 * ```
 */
export function calculateTotals(lines: ConsignmentLineItem[]): {
  totalWeight: number;
  totalVolume: number;
} {
  return lines.reduce(
    (acc, line) => ({
      totalWeight: acc.totalWeight + parseNumericValue(line.weight),
      totalVolume: acc.totalVolume + parseNumericValue(line.volume)
    }),
    { totalWeight: 0, totalVolume: 0 }
  );
}

/**
 * Safely parse numeric value from various input types
 *
 * Handles:
 * - Numbers (returned as-is)
 * - Numeric strings (parsed to float)
 * - Invalid/undefined/null values (returns 0)
 *
 * @param value - Value to parse (string, number, null, or undefined)
 * @returns Parsed numeric value or 0 if invalid
 *
 * @example
 * ```typescript
 * parseNumericValue(42)        // Returns: 42
 * parseNumericValue("42.5")    // Returns: 42.5
 * parseNumericValue("invalid") // Returns: 0
 * parseNumericValue(undefined) // Returns: 0
 * parseNumericValue(null)      // Returns: 0
 * ```
 */
export function parseNumericValue(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Values can be display-formatted (US locale) with a unit suffix, e.g.
    // "1,234.5 kg" or "10.00 m³". Strip thousands separators so parseFloat does
    // not stop at the first comma (which would read "1,234.5" as 1).
    const parsed = parseFloat(value.replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}
